import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  ExpansionPanel,
  ExpansionPanelSummary,
  ExpansionPanelDetails,
  FormLabel,
  Grid,
  makeStyles,
  Tooltip,
  Typography,
  withStyles,
} from '@material-ui/core';
import {
  isEmpty,
  some,
} from 'lodash';
import {
  Clear as DeleteIcon,
  DragIndicator,
  FiberManualRecord as FiberManualRecordIcon,
} from '@material-ui/icons';
import { useDrag, useDrop } from "react-dnd";
import IconButton from '@material-ui/core/IconButton';
import createSvgIcon from '@material-ui/icons/utils/createSvgIcon';
import { useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import compose from 'recompose/compose';
import { getI18n } from '../../../../../utils/intl';
import { DynamicForm } from '../../../../../components';
import styles from './styles';
import { Button } from '@material-ui/core';

const TEMPLATE_ERRORS_FILED_MISSING_REQUIRED_FIELD_VALUE = 'missingRequiredFields';
// workaround till we update react-scripts > 2.0.0, which supports adding svg images
const expandSVG = <path d="M12.023 16.9557L12.0229 16.9556L11.977 17L4.86808 10.1204C4.37731 9.64543 4.37731 8.87541 4.86808 8.40047C5.35884 7.92553 6.15453 7.92553 6.6453 8.40047L11.9771 13.5603L17.3547 8.3562C17.8455 7.88127 18.6412 7.88127 19.1319 8.3562C19.6227 8.83114 19.6227 9.60117 19.1319 10.0761L12.023 16.9557Z" />;

export const APIPolicyTemplate = (props) => {
  const useStyles = makeStyles(styles);
  const classes = useStyles();
  const {
    canMove,
    categoryId,
    categoryIndex,
    expandTemplate,
    handleChangeDynamicForm,
    index,
    item,
    onDeleteItem,
    onMoveItem,
    templateErrors,
    onCopyArguments,
    onPasteArguments,
    isClipboardAvailable,
  } = props;
  const intl = getI18n(useIntl());
  const [expanded, setExpanded] = useState(false);
  const hasMissingFieldsError =
    some(templateErrors, argError =>
      argError[TEMPLATE_ERRORS_FILED_MISSING_REQUIRED_FIELD_VALUE],
    );

  const onDelete = (e, item, i) => {
    e.stopPropagation();
    onDeleteItem(item, i);
  };

  const renderMissingFieldsError = () => {
    if (hasMissingFieldsError) {
      return (<div
        className={classes.missingFieldsContainer}
        data-apim-test='missing-fields-error'
      >
        <FiberManualRecordIcon className={classes.errorIcon} />
        <span className={classes.templateErrorLabel}>
          {intl.getI18nMessage('label.api.edit.policy.templates.policy.template.errors')}
        </span>
        <Tooltip
          arrow
          title={intl.getI18nMessage(
            'label.api.edit.policy.templates.policy.template.missing.required.fields.error.text',
          )}
        >
          <span className={classes.templateErrorText}>
            {intl.getI18nMessage(
              'label.api.edit.policy.templates.policy.template.missing.required.fields.link.text',
            )}
          </span>
        </Tooltip>
      </div>);
    }
    return <></>;
  }

  const renderUnavailableTemplateError = () => {
    if (!item.name) {
      return (<div
          className={classes.missingFieldsContainer}
          data-apim-test='unavailable-template-error'
        >
        <FiberManualRecordIcon className={classes.errorIcon} />
        <span className={classes.templateErrorLabel}>
          {intl.getI18nMessage('label.api.edit.policy.templates.policy.template.error')}
        </span>
        <Tooltip
          arrow
          title={intl.getI18nMessage(
            'label.api.edit.policy.templates.policy.template.unavailable.template.error.text',
          )}
        >
          <span className={classes.templateErrorText}>
            {intl.getI18nMessage(
              'label.api.edit.policy.templates.policy.template.unavailable.template.link.text',
            )}
          </span>
        </Tooltip>
      </div>);
    }
    return <></>;
  };

  const renderTemplateWarningsAndErrors = () => {
    return (<>
    {renderMissingFieldsError()}
    {renderUnavailableTemplateError()}
    </>);
  };

  const ref = useRef(null);
  const [{ handlerId }, drop] = useDrop({
    accept: ['template'],
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
      };
    },
    hover(item, monitor) {
      if (!ref.current || item.categoryId !== categoryId ||
        item.categoryIndex !== categoryIndex) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;
      if (dragIndex === hoverIndex) {
        return;
      }
      const hoverBoundingRect = ref.current?.getBoundingClientRect();
      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      const clientOffset = monitor.getClientOffset();
      const hoverClientY = clientOffset.y - hoverBoundingRect.top;
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }
      setTimeout(() => {
        onMoveItem(dragIndex, hoverIndex);
      });
      item.index = hoverIndex;
    },
  });
  const [, drag] = useDrag({
    canDrag: canMove,
    item: {
      categoryId,
      categoryIndex,
      index,
      type: 'template',
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });
  // const opacity = isDragging ? 0 : 1;
  drag(drop(ref));

  const ExpandIcon = createSvgIcon(expandSVG, 'expand');
  const toggleExpansion = (event, value) => {
    setExpanded(value);
  };

  useEffect(() => {
    setExpanded(expandTemplate);
  }, [expandTemplate]);

  return (
    <ExpansionPanel
      className="expand-panel-list-item"
      classes={{
        root: classes.panelRoot,
      }}
      data-apim-test={`template-panel-${index}`}
      expanded={expanded}
      onChange={toggleExpansion}
      key={item.uuid + index.toString()}
      square
    >
      <Box className={classes.summaryContainer}
        data-handler-id={handlerId}
        ref={ref}
      >
        <ExpansionPanelSummary
          aria-label="Expand"
          aria-controls="additional-actions1-content"
          classes={{
            content: classes.content,
            expandIcon: classes.expandIcon,
            root: classes.summaryRoot,
          }}
          data-apim-test={`template-${index}`}
          expandIcon={<ExpandIcon />}
          id="additional-actions1-header"
          key={item.uuid + index.toString() + '-summary'}
        >
          <IconButton
            className={canMove ? classes.iconRoot : classes.unDraggableTemplate}
            data-apim-test={`template-${index}-drag`}
            size="small"
          >
            <DragIndicator />
          </IconButton>
          <Grid container className={classes.labelContainer}>
            <Grid item>
              <FormLabel classes={{ root: classes.labelRoot }}>
                {item?.name || `ID: ${item?.uuid}`}
              </FormLabel>
              {renderTemplateWarningsAndErrors()}
            </Grid>
          </Grid>
        </ExpansionPanelSummary>
        <IconButton
          className={classes.iconRoot}
          data-apim-test={`template-${index}-delete`}
          size="small"
          onClick={e => onDelete(e, item, index)}
          onFocus={e => e.stopPropagation()}
        >
          <DeleteIcon />
        </IconButton>
      </Box>
      <ExpansionPanelDetails
        classes={{ root: classes.expansionPanelDetails }}
      >
        <div className={classes.expansionPanelDetailsContent}>
        <div className={classes.headerAndCopyPastePanel}>
          <Typography variant="h4" className={classes.h4NoMargin}>
            {item?.name}
          </Typography>
          {!isEmpty(item.arguments) &&
          <Tooltip
            title={intl.getI18nMessage(
              'label.api.edit.policy.templates.policy.template.arguments.copy.tooltip',
            )}
          >
            <div>
                <Button
                  className={classes.primaryButton}
                  variant="contained"
                  color="primary"
                  onClick={() => onCopyArguments(categoryIndex, index)}
                  disabled={item.arguments.every(arg => arg.secure)}
                >
                  {intl.getI18nMessage('label.api.edit.policy.templates.policy.template.arguments.copy')}
                </Button>
                <Button
                  className={classes.primaryButton}
                  variant="contained"
                  color="primary"
                  onClick={() => onPasteArguments(categoryIndex, index)}
                  disabled={!isClipboardAvailable}
                >
                  {intl.getI18nMessage('label.api.edit.policy.templates.policy.template.arguments.paste')}
                </Button>
              </div>
            </Tooltip>
          }
        </div>
          <Typography variant="body1">
            {item.description}
            {!isEmpty(item.arguments) &&
              <DynamicForm
                fields={item.arguments}
                handleChange={handleChangeDynamicForm}
                index={index}
              />
            }
          </Typography>
        </div>
      </ExpansionPanelDetails>
    </ExpansionPanel>
  )
};

const mapDispatchToProps = {
};

const mapStateToProps = () => ({
});

APIPolicyTemplate.propTypes = {
  canMove: PropTypes.bool,
  categoryId: PropTypes.string,
  categoryIndex: PropTypes.number,
  count: PropTypes.number,
  expandTemplate: PropTypes.bool,
  handleChangeDynamicForm: PropTypes.func,
  index: PropTypes.number,
  item: PropTypes.object,
  onDeleteItem: PropTypes.func,
  onMoveItem: PropTypes.func,
  templateErrors: PropTypes.array,
  onCopyArguments: PropTypes.func,
  onPasteArguments: PropTypes.func,
  isClipboardAvailable: PropTypes.bool,
};

APIPolicyTemplate.displayName = 'PolicyTemplate';

export default compose(
  withStyles(styles),
  connect(mapStateToProps, mapDispatchToProps),
)(APIPolicyTemplate);
